import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG } from '../config/api';

// Flag to prevent multiple redirects
let isRedirecting = false;

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

// Function to clear auth state completely
const clearAuthState = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('auth-storage');
};

// Reset redirect flag - called when navigating to login/invite pages
export const resetRedirectFlag = () => {
  isRedirecting = false;
};

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Reset redirect flag when on login or invite pages
    const path = window.location.pathname;
    if (path.includes('/login') || path.includes('/invite')) {
      isRedirecting = false;
    }

    // Don't add token for login/register routes
    const isLoginRoute = config.url?.includes('/auth/login') || config.url?.includes('/auth/register');
    
    if (!isLoginRoute) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    // Successful response means we're not in a redirect state
    isRedirecting = false;
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Don't try to refresh for login/register/refresh routes
    const url = originalRequest?.url || '';
    const isLoginRoute = url.includes('/auth/login') || url.includes('/auth/register');
    const isRefreshRoute = url.includes('/auth/refresh');
    const isMeRoute = url.includes('/auth/me');
    
    // For login route 401, just reject - wrong credentials
    if (isLoginRoute || isRefreshRoute) {
      return Promise.reject(error);
    }

    // Handle 401 - try to refresh token
    if (error.response?.status === 401 && !originalRequest?._retry) {
      // For /auth/me during initialization, just reject without redirect
      // The auth store handles this gracefully
      if (isMeRoute) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      // If no refresh token, clear and redirect
      if (!refreshToken) {
        if (!isRedirecting && !window.location.pathname.includes('/login')) {
          isRedirecting = true;
          clearAuthState();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const response = await axios.post(`${API_CONFIG.baseUrl}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        isRefreshing = false;
        onRefreshed(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        isRefreshing = false;
        refreshSubscribers = [];
        
        // Refresh failed - logout and redirect
        if (!isRedirecting && !window.location.pathname.includes('/login')) {
          isRedirecting = true;
          clearAuthState();
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const apiGet = <T>(url: string, params?: Record<string, unknown>) =>
  api.get<T>(url, { params }).then((res) => res.data);

export const apiPost = <T>(url: string, data?: unknown) =>
  api.post<T>(url, data).then((res) => res.data);

export const apiPut = <T>(url: string, data?: unknown) =>
  api.put<T>(url, data).then((res) => res.data);

export const apiPatch = <T>(url: string, data?: unknown) =>
  api.patch<T>(url, data).then((res) => res.data);

export const apiDelete = <T>(url: string) =>
  api.delete<T>(url).then((res) => res.data);

// Default export for backward compatibility
export default api;
